import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Download,
  FileClock,
  Loader2,
  Plus,
  RotateCcw,
  Scale,
  Sparkles,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { actionApi, actionError } from '../api';
import type { ArtOfRecord, ProsecutionTimeline as Timeline } from '../types';

/**
 * What happened in this prosecution, as against what is in the file.
 *
 * The wrapper listing is a table of contents. It says a non-final rejection was mailed on
 * a date, which tells an attorney picking up a matter nothing: what was rejected, over
 * what art, what was argued back, and what changed in the claims each round are all
 * inside the papers. So the papers are read.
 *
 * Asked for rather than run on load — reading a file wrapper means OCRing the scans in it
 * and a model call per exchange — and kept once read.
 *
 * Used on two surfaces: a matter in /action, and an application on the USPTO Data page
 * that nobody has opened a response for. The question is the same one, so the component
 * and everything behind it are the same.
 */
/**
 * One piece of art of record, whatever shape it was stored in.
 *
 * The worker keeps these as {reference, citedFor}. Records written before it did hold the
 * Python repr of that object — "{'reference': 'Sorkhabi', 'citedFor': '…'}" — which the
 * page showed verbatim, braces and quotes and all. Rather than ask anyone to read a
 * prosecution again, that form is recognised and unpacked here.
 */
function readArt(art: ArtOfRecord | string): ArtOfRecord {
  if (typeof art !== 'string') return art;

  const quoted = String.raw`'((?:[^'\\]|\\.)*)'`;
  const repr = new RegExp(
    `^\\{\\s*'reference':\\s*${quoted}\\s*,\\s*'citedFor':\\s*${quoted}\\s*\\}$`
  ).exec(art.trim());
  if (repr) {
    const unescape = (value: string) => value.replace(/\\(.)/g, '$1');
    return { reference: unescape(repr[1]), citedFor: unescape(repr[2]) };
  }
  return { reference: art };
}

export default function ProsecutionTimeline({
  matterId,
  applicationNumber,
  compact,
  onAddArt,
  artInActive,
}: {
  /** A matter in /action. Give this or `applicationNumber`, not both. */
  matterId?: string;
  /** An application with no matter — the USPTO Data page. */
  applicationNumber?: string;
  compact?: boolean;
  /**
   * Put a piece of art of record into the caller's own working set, where the caller has
   * one. Given by the USPTO Data page, whose session has active documents; not by
   * /action, whose references panel is where art is collected instead.
   *
   * Passed in rather than done here so this stays a component that reads a prosecution:
   * what "active" means, and the quotas and plan around it, belong to the page that has
   * the notion. Offered only for art carrying a document number — there is nothing to
   * fetch by otherwise.
   */
  onAddArt?: (art: ArtOfRecord) => void;
  /** Whether that piece of art is already in the working set. */
  artInActive?: (art: ArtOfRecord) => boolean;
}) {
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [runAt, setRunAt] = useState('');
  const [loading, setLoading] = useState(true);
  /** '' when idle, otherwise which kind of run is going. */
  const [running, setRunning] = useState<'' | 'update' | 'full'>('');
  const [error, setError] = useState('');
  /** '' when idle, otherwise the key of the art row being fetched. */
  const [fetching, setFetching] = useState('');
  const alive = useRef(true);

  // Set on the way IN as well as out.
  //
  // Without the assignment on mount this is a permanent off-switch: React runs effects
  // mount → unmount → mount in development, the cleanup sets it false, and nothing ever
  // sets it back. Every state write after that is skipped, so the panel says "Loading…"
  // for ever and the button that runs the reading is never drawn.
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const result = matterId
        ? await actionApi.getTimeline(matterId)
        : await actionApi.getApplicationTimeline(applicationNumber ?? '');
      if (!alive.current) return result;
      setTimeline(result.timeline);
      setRunAt(result.runAt);
      return result;
    } catch (err) {
      if (alive.current) setError(actionError(err));
      return null;
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [matterId, applicationNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (mode: 'update' | 'full' = 'update') => {
    setRunning(mode);
    setError('');
    try {
      if (matterId) await actionApi.generateTimeline(matterId, mode);
      else await actionApi.generateApplicationTimeline(applicationNumber ?? '', mode);
      // Nothing to poll but the result itself: this has no session status behind it, so
      // the reading is collected by asking again until it is newer than what is shown.
      const before = runAt;
      const deadline = Date.now() + 900000;
      while (Date.now() < deadline && alive.current) {
        await new Promise(resolve => setTimeout(resolve, 4000));
        const result = await load();
        if (result?.runAt && result.runAt !== before) break;
      }
    } catch (err) {
      if (alive.current) setError(actionError(err));
    } finally {
      if (alive.current) setRunning('');
    }
  };

  /**
   * One piece of art of record, as published.
   *
   * The failure worth showing is the one where the number resolved but no source carries
   * the document — a foreign application the image server does not hold, most often. That
   * is a fact about the document rather than a fault, so it is reported next to the art
   * and does not disturb the reading.
   */
  const fetchArt = async (key: string, row: ArtOfRecord) => {
    setFetching(key);
    setError('');
    try {
      await actionApi.downloadArt(row);
    } catch (err) {
      if (alive.current) setError(actionError(err));
    } finally {
      if (alive.current) setFetching('');
    }
  };

  const entries = timeline?.entries ?? [];
  const summary = timeline?.summary ?? {};

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start gap-3">
        <FileClock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900">
            What happened in this prosecution
          </h3>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-600">
            {running === 'full'
              ? 'Reading every paper in the file wrapper again, round by round. On a long file this takes a few minutes.'
              : running === 'update'
              ? 'Reading the papers filed since this last ran. Everything already read is kept.'
              : loading
                ? 'Checking whether this prosecution has been read before…'
                : entries.length > 0
                  ? `${entries.length} exchange${entries.length === 1 ? '' : 's'}${
                      typeof timeline?.added === 'number' && timeline.added < entries.length
                        ? `, ${timeline.added} read on the last run`
                        : ' read'
                    }${runAt ? `, ${new Date(runAt).toLocaleString()}` : ''}.`
                  : 'Nothing is read until you ask. The file wrapper lists what was filed; this reads the papers and says what was rejected, what was argued, what changed in the claims each round, and where the case stands now — a model call per exchange, so it is run on request.'}
          </p>
        </div>
        <span className="flex shrink-0 flex-wrap items-center gap-1.5">
          {/* The ordinary case once a timeline exists: prosecution grows one exchange at
              a time, and re-reading eleven rounds to learn what the twelfth says is
              eleven OCR passes spent reproducing the record. */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => run('update')}
            disabled={Boolean(running) || loading}
            title={
              entries.length > 0
                ? 'Read only the papers filed since this last ran'
                : 'Read the prosecution'
            }
          >
            {running === 'update' ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            )}
            {entries.length > 0 ? 'Update' : 'Read the prosecution'}
          </Button>

          {/* Everything again — for a file whose earlier rounds were read badly, or read
              before an improvement to how they are read. */}
          {entries.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => run('full')}
              disabled={Boolean(running) || loading}
              title="Read every exchange again from the beginning"
            >
              {running === 'full' ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              )}
              Read it all again
            </Button>
          )}
        </span>
      </header>

      {error && (
        <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {/* Where the case stands. First, because it is the one thing someone picking up a
          file needs and the rest is the working behind it. */}
      {(summary.wherefore || summary.claimStory) && (
        <section className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
          {summary.wherefore && (
            <>
              <h4 className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Where it stands
              </h4>
              <p className="mt-1 text-sm leading-relaxed text-slate-700">
                {summary.wherefore}
              </p>
            </>
          )}
          {summary.claimStory && (
            <>
              <h4 className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                The claims, across the file
              </h4>
              <p className="mt-1 text-sm leading-relaxed text-slate-700">
                {summary.claimStory}
              </p>
            </>
          )}
          {(summary.openIssues ?? []).length > 0 && (
            <>
              <h4 className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                Still open
              </h4>
              <ul className="mt-1 space-y-0.5">
                {(summary.openIssues ?? []).map(issue => (
                  <li key={issue} className="flex items-start gap-1.5 text-sm text-slate-700">
                    <Scale className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
                    {issue}
                  </li>
                ))}
              </ul>
            </>
          )}
          {(summary.artOfRecord ?? []).length > 0 && (
            <>
              <h4 className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                Art of record
              </h4>
              <ul className="mt-1 space-y-1">
                {(summary.artOfRecord ?? []).map((art, index) => {
                  const row = readArt(art);
                  const key = `${row.reference}-${index}`;
                  return (
                    <li key={key} className="text-sm">
                      <span className="font-medium text-slate-800">{row.reference}</span>
                      {row.citedFor && (
                        <span className="text-slate-600"> — {row.citedFor}</span>
                      )}
                      {/* Offered only where the citation carried a number. Art named by
                          its author alone is non-patent literature with nothing to fetch
                          by; the reference panel searches the web for those. Saying so is
                          better than an absence the reader has to interpret. */}
                      {!row.number && (
                        <span className="ml-2 align-baseline text-xs text-slate-400">
                          cited without a number
                        </span>
                      )}
                      {row.number && (
                        <button
                          type="button"
                          disabled={fetching === key}
                          onClick={() => fetchArt(key, row)}
                          className="ml-2 inline-flex items-center gap-1 align-baseline text-xs
                                     text-blue-600 hover:text-blue-700 hover:underline
                                     disabled:cursor-not-allowed disabled:text-slate-400"
                          title={`Download ${row.reference} as published`}
                        >
                          {fetching === key ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Download className="h-3 w-3" />
                          )}
                          PDF
                        </button>
                      )}
                      {/* Art the examiner relied on is usually art worth asking about,
                          and the number is already resolved — so the step between
                          reading the file and working with the reference is one click. */}
                      {row.number && onAddArt && (
                        artInActive?.(row) ? (
                          <span className="ml-2 inline-flex items-center gap-1 align-baseline
                                           text-xs text-green-600">
                            <CheckCircle2 className="h-3 w-3" />
                            In active
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onAddArt(row)}
                            className="ml-2 inline-flex items-center gap-1 align-baseline text-xs
                                       text-blue-600 hover:text-blue-700 hover:underline"
                            title={`Add ${row.reference} to your active documents`}
                          >
                            <Plus className="h-3 w-3" />
                            Add to active
                          </button>
                        )
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
      )}

      {/* The exchanges, oldest first — prosecution is read forwards, because the claims
          narrowed because of what the examiner said the round before. */}
      {entries.length > 0 && (
        <ol className="space-y-0">
          {entries.map((entry, index) => (
            <li key={`${entry.date}-${index}`} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                    entry.actor === 'office'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-sky-100 text-sky-700'
                  }`}
                  title={entry.actor === 'office' ? 'The Office' : 'The applicant'}
                >
                  {entry.actor === 'office' ? (
                    <Building2 className="h-3 w-3" />
                  ) : (
                    <User className="h-3 w-3" />
                  )}
                </span>
                {index < entries.length - 1 && (
                  <span className="my-1 w-px flex-1 bg-slate-200" />
                )}
              </div>

              <div className="min-w-0 flex-1 pb-5">
                <p className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-mono text-xs text-slate-400">{entry.date}</span>
                  <span className="text-sm font-medium text-slate-900">{entry.title}</span>
                </p>

                {entry.unreadable ? (
                  <p className="mt-0.5 flex items-start gap-1 text-xs text-amber-700">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    {entry.summary}
                  </p>
                ) : (
                  <p className="mt-0.5 text-sm leading-relaxed text-slate-700">
                    {entry.summary}
                  </p>
                )}

                {/* The one thread nobody can reconstruct without reading every claim
                    set, so it is given its own line rather than buried in the prose. */}
                {entry.claimChanges && (
                  <p className="mt-1 rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs leading-relaxed text-slate-600">
                    <span className="font-medium text-slate-500">Claims: </span>
                    {entry.claimChanges}
                  </p>
                )}

                {!compact && (entry.documents ?? []).length > 0 && (
                  <p className="mt-1 flex flex-wrap gap-1">
                    {(entry.documents ?? []).map(document => (
                      <span
                        key={document.docId}
                        className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500"
                        title={document.description}
                      >
                        {document.documentCode}
                      </span>
                    ))}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {timeline?.note && (
        <p className="text-xs text-slate-400">{timeline.note}.</p>
      )}
    </div>
  );
}
